import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function PortfolioBetaCalculator() {
  const [weights, setWeights] = useState("");
  const [betas, setBetas] = useState("");
  const [result, setResult] = useState<{ beta: number; interpretation: string } | null>(null);

  const calculate = () => {
    const weightArray = weights.split(",").map(w => parseFloat(w.trim()) / 100);
    const betaArray = betas.split(",").map(b => parseFloat(b.trim()));

    if (weightArray.some(isNaN) || betaArray.some(isNaN) || 
        weightArray.length !== betaArray.length || weightArray.length === 0) {
      toast.error("Please enter valid values with matching array lengths");
      return;
    }

    const weightSum = weightArray.reduce((sum, w) => sum + w, 0);
    if (Math.abs(weightSum - 1) > 0.01) {
      toast.error("Weights must sum to 100%");
      return;
    }

    // Portfolio Beta = Σ(Weight × Beta)
    const portfolioBeta = weightArray.reduce((sum, weight, index) => {
      return sum + (weight * betaArray[index]);
    }, 0);

    let interpretation = "";
    if (portfolioBeta < 0) interpretation = "Inverse correlation with market";
    else if (portfolioBeta < 0.8) interpretation = "Less volatile than market (defensive)";
    else if (portfolioBeta < 1.2) interpretation = "Similar volatility to market";
    else interpretation = "More volatile than market (aggressive)";

    setResult({ beta: portfolioBeta, interpretation });
    toast.success("Portfolio Beta calculated!");
  };

  const reset = () => {
    setWeights("");
    setBetas("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Portfolio Beta Calculator</CardTitle>
          <CardDescription>
            Measure systematic risk and market correlation of your portfolio
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="weights">Asset Weights (%, comma-separated, must sum to 100%)</Label>
            <Input
              id="weights"
              type="text"
              placeholder="40, 30, 20, 10"
              value={weights}
              onChange={(e) => setWeights(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="betas">Asset Betas (comma-separated)</Label>
            <Input
              id="betas"
              type="text"
              placeholder="1.2, 0.9, 1.5, 0.7"
              value={betas}
              onChange={(e) => setBetas(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere">
                  <strong>Portfolio Beta:</strong> {result.beta.toFixed(3)}
                </p>
                <CopyResultButton value={result.beta.toFixed(3)} />
              </div>
              <p className="text-sm">
                <strong>Risk Profile:</strong> {result.interpretation}
              </p>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["portfolio-beta"]} trigger={result?.beta} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
